import React, { useEffect, useState } from 'react';
import { AlertTriangle, Save } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { useLocations } from '../../hooks/locations/useLocations';
import {
  useBioAgents,
  useCreateBioTerminal,
  useUpdateBioTerminal,
} from '../../hooks/useBio';
import type { BioTerminal } from '../../types/bio.types';

interface BioTerminalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null — регистрация нового терминала. */
  terminal: BioTerminal | null;
}

const errorMessage = (error: unknown): string => {
  const apiError = error as { message?: string } | null;
  return apiError?.message || 'Не удалось сохранить терминал';
};

export function BioTerminalDialog({
  open,
  onOpenChange,
  terminal,
}: BioTerminalDialogProps) {
  const isEdit = terminal !== null;
  const { data: locations } = useLocations({ limit: 100 });
  const { data: agents } = useBioAgents();
  const createTerminal = useCreateBioTerminal();
  const updateTerminal = useUpdateBioTerminal();
  const isSaving = createTerminal.isPending || updateTerminal.isPending;

  const [serialNumber, setSerialNumber] = useState('');
  const [model, setModel] = useState('');
  const [locationId, setLocationId] = useState('');
  const [agentId, setAgentId] = useState('');
  const [host, setHost] = useState('');
  const [port, setPort] = useState(80);
  const [username, setUsername] = useState('admin');
  const [password, setPassword] = useState('');
  const [direction, setDirection] = useState<'in' | 'out' | 'any'>('any');
  const [debounceSeconds, setDebounceSeconds] = useState(60);
  const [capacityFaces, setCapacityFaces] = useState('');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (!open) return;
    setSerialNumber(terminal?.serial_number ?? '');
    setModel(terminal?.model ?? '');
    setLocationId(terminal?.location_id ?? '');
    setAgentId(terminal?.agent_id ?? '');
    setHost(terminal?.host ?? '');
    setPort(terminal?.port ?? 80);
    setUsername(terminal?.username ?? 'admin');
    setPassword('');
    setDirection(terminal?.direction ?? 'any');
    setDebounceSeconds(terminal?.debounce_seconds ?? 60);
    setCapacityFaces(
      terminal?.capacity_faces != null ? String(terminal.capacity_faces) : '',
    );
    setIsActive(terminal?.is_active ?? true);
    createTerminal.reset();
    updateTerminal.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, terminal]);

  const handleSubmit = async () => {
    const capacity = capacityFaces.trim()
      ? Number(capacityFaces.trim())
      : undefined;
    try {
      if (isEdit) {
        await updateTerminal.mutateAsync({
          id: terminal.id,
          data: {
            location_id: locationId,
            agent_id: agentId || undefined,
            model: model || undefined,
            host,
            port,
            username,
            // Пустой пароль — не менять сохранённый.
            password: password || undefined,
            direction,
            debounce_seconds: debounceSeconds,
            capacity_faces: capacity,
            is_active: isActive,
          },
        });
      } else {
        await createTerminal.mutateAsync({
          serial_number: serialNumber.trim(),
          location_id: locationId,
          agent_id: agentId || undefined,
          model: model || undefined,
          host: host.trim(),
          port,
          username: username.trim(),
          password,
          direction,
          debounce_seconds: debounceSeconds,
          capacity_faces: capacity,
        });
      }
      onOpenChange(false);
    } catch {
      // Текст ошибки показывается ниже — диалог остаётся открытым.
    }
  };

  const error = createTerminal.error || updateTerminal.error;
  const canSubmit =
    !isSaving &&
    locationId.length > 0 &&
    host.trim().length > 0 &&
    username.trim().length > 0 &&
    (isEdit || (serialNumber.trim().length > 0 && password.length > 0));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div>
            <DialogTitle>
              {isEdit ? `Терминал ${terminal.serial_number}` : 'Новый терминал'}
            </DialogTitle>
            <DialogDescription>
              Данные для доступа к устройству по ISAPI. Пароль хранится
              зашифрованным и передаётся только агенту объекта.
            </DialogDescription>
          </div>
          {!isSaving && <DialogClose onClose={() => onOpenChange(false)} />}
        </DialogHeader>

        <div className="max-h-[60vh] space-y-4 overflow-y-auto py-2 pr-1">
          {!isEdit && (
            <div>
              <Label htmlFor="bio-terminal-serial">Серийный номер</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Как его отдаёт устройство — по нему сопоставляются события
              </p>
              <Input
                id="bio-terminal-serial"
                value={serialNumber}
                onChange={(e) => setSerialNumber(e.target.value)}
                placeholder="DS-K1T343M(L)20240625V032100ENGB2265618"
                disabled={isSaving}
              />
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="bio-terminal-location">Объект</Label>
              <select
                id="bio-terminal-location"
                className="mt-2 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
                disabled={isSaving}
              >
                <option value="">— выберите объект —</option>
                {(locations?.data ?? []).map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="bio-terminal-agent">Агент</Label>
              <select
                id="bio-terminal-agent"
                className="mt-2 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                value={agentId}
                onChange={(e) => setAgentId(e.target.value)}
                disabled={isSaving}
              >
                <option value="">— без агента —</option>
                {(agents ?? []).map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                При привязке сервер сам поставит команду синхронизации времени
              </p>
            </div>

            <div>
              <Label htmlFor="bio-terminal-host">Адрес в локальной сети</Label>
              <Input
                id="bio-terminal-host"
                className="mt-2"
                value={host}
                onChange={(e) => setHost(e.target.value)}
                placeholder="192.168.100.29"
                disabled={isSaving}
              />
            </div>

            <div>
              <Label htmlFor="bio-terminal-port">Порт</Label>
              <Input
                id="bio-terminal-port"
                className="mt-2"
                type="number"
                min={1}
                max={65535}
                value={port}
                onChange={(e) => setPort(parseInt(e.target.value, 10) || 80)}
                disabled={isSaving}
              />
            </div>

            <div>
              <Label htmlFor="bio-terminal-username">Логин ISAPI</Label>
              <Input
                id="bio-terminal-username"
                className="mt-2"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="admin"
                disabled={isSaving}
              />
            </div>

            <div>
              <Label htmlFor="bio-terminal-password">Пароль ISAPI</Label>
              <Input
                id="bio-terminal-password"
                className="mt-2"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isEdit ? '••••••••' : ''}
                disabled={isSaving}
              />
              {isEdit && (
                <p className="text-xs text-muted-foreground mt-1">
                  Пусто — оставить прежний
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="bio-terminal-model">Модель</Label>
              <Input
                id="bio-terminal-model"
                className="mt-2"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                placeholder="DS-K1T343M(L)"
                disabled={isSaving}
              />
            </div>

            <div>
              <Label htmlFor="bio-terminal-direction">Назначение</Label>
              <select
                id="bio-terminal-direction"
                className="mt-2 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                value={direction}
                onChange={(e) =>
                  setDirection(e.target.value as 'in' | 'out' | 'any')
                }
                disabled={isSaving}
              >
                <option value="any">Универсальный — вход и выход</option>
                <option value="in">Только вход</option>
                <option value="out">Только выход</option>
              </select>
            </div>

            <div>
              <Label htmlFor="bio-terminal-debounce">
                Окно дребезга, секунд
              </Label>
              <Input
                id="bio-terminal-debounce"
                className="mt-2"
                type="number"
                min={0}
                max={600}
                value={debounceSeconds}
                onChange={(e) =>
                  setDebounceSeconds(parseInt(e.target.value, 10) || 0)
                }
                disabled={isSaving}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Повторные распознавания в этом окне — одна отметка
              </p>
            </div>

            <div>
              <Label htmlFor="bio-terminal-capacity">Ёмкость лиц</Label>
              <Input
                id="bio-terminal-capacity"
                className="mt-2"
                type="number"
                min={1}
                value={capacityFaces}
                onChange={(e) => setCapacityFaces(e.target.value)}
                placeholder="1500"
                disabled={isSaving}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Из паспорта модели; пусто — неизвестна, предупреждений не будет
              </p>
            </div>
          </div>

          {isEdit && (
            <div className="flex items-center justify-between border-t pt-3">
              <div className="space-y-0.5">
                <Label htmlFor="bio-terminal-active">Активен</Label>
                <p className="text-xs text-muted-foreground">
                  Выключенный терминал не выдаётся агенту в конфиге
                </p>
              </div>
              <Switch
                id="bio-terminal-active"
                checked={isActive}
                onCheckedChange={setIsActive}
                disabled={isSaving}
              />
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 rounded-md bg-red-100 p-3 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>{errorMessage(error)}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Отмена
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            <Save className="mr-2 h-4 w-4" />
            {isEdit ? 'Сохранить' : 'Зарегистрировать'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
